import { classNames } from "@circle-react/helpers/twMergeWithCN";
import { Typography } from "@circleco/compass/components/Typography";
import { useRef, useState } from "react";

import { t } from "@/i18n-js/instance";
import type { FileUploadState } from "@/react/hooks/useParallelDirectUploads";

import { CircularProgress } from "../../Shared/CircularProgress";
import { useImagePostModalStore } from "../../store";
import { CroppingOptions } from "./CroppingOptions";
import { MainImage } from "./MainImage";
import { Thumbnails } from "./Thumbnails";
import { ZoomSlider } from "./ZoomSlider";

type UploadStatus = "completed" | "error" | "idle" | "uploading";

const toUploadStatus = (status: string): UploadStatus => {
  switch (status) {
    case "completed":
    case "error":
    case "idle":
    case "uploading":
      return status;
    default:
      return "idle";
  }
};

interface CarouselProps {
  editingImageSignedId?: string;
  fileStates: FileUploadState[];
  onDeleteAllImages: () => void;
  overallProgress: number;
  status: string;
}
export const Carousel = ({
  status,
  overallProgress,
  fileStates,
  editingImageSignedId,
  onDeleteAllImages,
}: CarouselProps) => {
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const { selectedFiles, uploadedImages } = useImagePostModalStore();
  const currentImage = selectedFiles[currentImageIndex];
  const uploadStatus = toUploadStatus(status);
  const prevLengthRef = useRef(selectedFiles.length);
  const prevLength = prevLengthRef.current;
  const newLength = selectedFiles.length;
  const shouldShowCroppingOptions =
    uploadedImages.length === 0 && currentImageIndex === 0;

  // if the last image is deleted, move to the new last image.
  if (newLength > 0 && currentImageIndex > newLength - 1) {
    setCurrentImageIndex(selectedFiles.length - 1);
  } // If new images are added, update the currentImageIndex to the first new image
  else if (newLength > prevLength) {
    setCurrentImageIndex(prevLength);
  }

  // update the reference to the new length
  prevLengthRef.current = newLength;

  if (!currentImage) return null;

  return (
    <>
      <div
        className={classNames(
          "border-primary flex min-h-0 grow flex-col overflow-hidden rounded-md border",
          { "bg-tertiary": uploadStatus === "uploading" },
        )}
      >
        {uploadStatus === "idle" && (
          <>
            <MainImage currentImage={currentImage} />
            <div
              className={classNames(
                "border-primary bg-secondary flex border-t px-4 py-3",
                {
                  "justify-between": shouldShowCroppingOptions,
                  "justify-end": !shouldShowCroppingOptions,
                },
              )}
            >
              {shouldShowCroppingOptions && <CroppingOptions />}
              <ZoomSlider currentImage={currentImage} />
            </div>
          </>
        )}
        {(uploadStatus === "uploading" || uploadStatus === "completed") && (
          <div className="mb-12 mt-1 flex aspect-video flex-col items-center justify-center gap-4">
            <CircularProgress
              percentage={overallProgress}
              size={22}
              strokeWidth={1.75}
            />
            <Typography variant="body-sm" color="secondary">
              {t("image_space.post_modal.steps.adjust_images.uploading")}
            </Typography>
          </div>
        )}
        {uploadStatus === "error" && (
          <div className="mb-12 mt-1 flex aspect-video flex-col items-center justify-center gap-4">
            <Typography variant="label-md" color="primary">
              {t("image_space.post_modal.steps.adjust_images.files_error")}
            </Typography>
          </div>
        )}
      </div>
      <Thumbnails
        currentImageIndex={currentImageIndex}
        selectedFiles={selectedFiles}
        setCurrentImageIndex={setCurrentImageIndex}
        fileStates={fileStates}
        status={uploadStatus}
        editingImageSignedId={editingImageSignedId}
        onDeleteAllImages={onDeleteAllImages}
      />
    </>
  );
};
